<template>
  <div>
    <collapsible-card
        :id="'slot_'+conn_idx"
        :collapsible="false"
        :class="{ 'valid-slot': license.isValidSlot(conn, conn_idx) }"
    >
      <!-- === Синхронизировать с License === -->
      <template v-slot:badge>
        <b-icon-clock-fill
            v-if="connExpired(conn)"
            variant="warning"
            class="mr-1"
        ></b-icon-clock-fill>
        {{ connTypeWithState(conn, conn_idx) }}
      </template>

      <!-- <== Конец синхронизации ====== -->

      <template v-slot:second-badge v-if="showSecondBadge()">
        {{ conn.entities.length }}/{{ license.data.package.min_items }}
      </template>

      <template v-slot:description>
        <b-container>
          <transition name="fade">
            <!-- Локальные имена -->
            <div v-if="isLocalNames()" key="one_year_trial">
              <b-row>
                <b-col cols="12" class="text-center mb-4">
                  <template v-if="isPersonalTrial()">
                    <template v-if="isNeverSaved()">
                      <h5 class="mb-2">{{ $t('license.locals.pers_h_new') }}</h5>
                      <h5 class="mb-4 text-secondary">{{ $t('license.locals.pers_warn') }}</h5>
                    </template>
                    <template v-else-if="isExpired()">
                      <h5 class="mb-2">{{ $t('license.locals.pers_corp_h_exp') }}</h5>
                      <h5 class="mb-4 text-secondary">{{ $t('license.locals.pers_warn') }}</h5>
                    </template>
                    <template v-else>
                      <h5 class="mb-2">{{ $t('license.locals.pers_h') }}</h5>
                      <h5 class="mb-4 text-secondary">{{ $t('license.locals.pers_warn') }}</h5>
                    </template>
                  </template>
                  <template v-else>
                    <template v-if="isNeverSaved()">
                      <h5 class="mb-2">{{ $t('license.locals.corp_h_new') }}</h5>
                      <h5 class="mb-4 text-secondary">{{ $t('license.locals.corp_warn') }}</h5>
                    </template>
                    <template v-else-if="isExpired()">
                      <h5 class="mb-2">{{ $t('license.locals.pers_corp_h_exp') }}</h5>
                      <h5 class="mb-4 text-secondary">{{ $t('license.locals.corp_warn') }}</h5>
                    </template>
                    <template v-else>
                      <h5 class="mb-2">{{ $t('license.locals.corp_h') }}</h5>
                      <h5 class="mb-4 text-secondary">{{ $t('license.locals.corp_warn') }}</h5>
                    </template>
                  </template>

                  <template v-if="isAnyTypeTrial()">
                    <template v-if="isExpired()">
                      <div class="mb-2">{{ $t('license.locals.trial_details_exp', [conValidUntilDate(), conValidUntilDistance()]) }}</div>
                    </template>
                    <template v-else>
                      <div class="mb-2">{{ $t('license.locals.trial_details', [conValidUntilFormatted()]) }}</div>
                    </template>
                  </template>
                  <template v-else>
                    <div class="mb-2">{{ $t('license.locals.paid_details', [conValidUntilFormatted()]) }}</div>
                  </template>

                  <div>{{ $t('license.locals.hosts') }}</div>
                  <div>{{ $t('license.locals.dbs') }}</div>

                  <template v-if="isPersonalTrial()">
                    <template v-if="isExpired()">
                      <div class="mt-4 mb-4">
                        <div class="text-primary">{{ $t('license.locals.pers_trial_renew') }}</div>
                        <code>./rdbm -c db.cfg license</code>
                      </div>
                    </template>
                  </template>
                  <div v-if="isPersonalTrial()" class="mt-4" v-html="$t('license.locals.pers_trial_warning')"></div>
                  <template v-else-if="isNonPersonalTrial()">
                    <template v-if="isExpired()">
                      <div class="mt-4" v-html="$t('license.locals.corp_trial_warning_exp')"></div>
                    </template>
                    <template v-else>
                      <div class="mt-4" v-html="$t('license.locals.corp_trial_warning')"></div>
                    </template>
                  </template>
                  <div v-else class="mt-4" v-html="$t('license.locals.corp_paid_warning')"></div>
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="12" class="text-center">
                  <b-button
                      variant="primary"
                      :disabled="!license.isValidSlot(conn, conn_idx)"
                      @click="handleAddEntityClick(conn)"
                  >
                    <b-icon-plus aria-hidden="true" class="mr-1"></b-icon-plus>
                    {{ $t('license.locals.add') }}
                  </b-button>
                </b-col>
              </b-row>
            </div>

            <!-- Обычное редактирование -->
            <div v-else key="regular_slot">
              <transition-group name="fade" class="row">
                <b-col
                    cols="12"
                    md="6"
                    v-for="(entity, entity_idx) in conn.entities"
                    :key="entity.id"
                >
                  <!-- === Редактирование сущьности ============= -->
                  <entity-input
                      :ref="'entity_'+entity_idx"
                      :license="license"
                      :entity="entity"
                      :entity_idx="entity_idx"
                      :conn="conn"
                      :conn_idx="conn_idx"
                      @focus-last-entity="$emit('focus-last-entity')"
                  ></entity-input>
                </b-col>
                <!-- === Кнопка "Добавить" ====================== -->
                <!-- Сначала "обычный" вариант, когда слот может быть расширен -->
                <b-col v-if="canAddEntity()" key="-20000000">
                  <div class="add-btn-wrapper">
                    <div class="table-cell">
                      <!-- Кнопка со счетчиком оставшихся уже оплаченных -->
                      <b-button
                          v-if="conPaidNamesRemaining() > 0"
                          variant="primary"
                          :disabled="!license.isValidSlot(conn, conn_idx)"
                          @click="handleAddEntityClick(conn, conn_idx)"
                      >
                        <b-icon-plus aria-hidden="true" class="mr-1"></b-icon-plus
                        >
                        {{ $t('license.slot.add_remaining', [conPaidNamesRemaining()]) }}
                      </b-button
                      >
                      <!-- Обычная кнопка -->
                      <b-button
                          v-else
                          aria-hidden="true"
                          class="mr-1"
                          variant="primary"
                          :disabled="!license.isValidSlot(conn, conn_idx)"
                          @click="handleAddEntityClick(conn, conn_idx)"
                      >
                        <b-icon-plus aria-hidden="true" class="mr-1"></b-icon-plus
                        >
                        {{ $t('license.slot.add') }}
                      </b-button
                      >
                    </div>
                    <div class="d-table-cell v-center">
                      <div
                          v-if="conPaidNamesRemaining() > 0"
                          class="ml-3 text-secondary t85p"
                      >{{
                          $t('license.slot.remaining_note', [license.data.package.min_items, conPaidNamesRemaining()])
                        }}
                      </div>
                    </div>
                  </div>
                </b-col>
                <!-- Теперь кнопка-заменитель кнопки "Добавить новый слот" -->
                <b-col v-else-if="license.data.connections.length - conn_idx == 1" key="-20000001">
                  <div class="add-btn-wrapper">
                    <div class="table-cell">
                      <b-button
                          variant="primary"
                          :disabled="!license.isValidSlot(conn, conn_idx)"
                          @click="handleAddSlotClick(conn_idx)"
                      >
                        <b-icon-plus aria-hidden="true" class="mr-1"></b-icon-plus
                        >
                        {{ $t('license.slot.add') }}
                      </b-button>
                    </div>
                  </div>
                </b-col>
              </transition-group>
              <!-- === Предупреждение для зашифрованных ============ -->
              <transition name="fade">
                <div v-if="hasUnencryptedNames()" class="mt-3">
                  <b-icon-info-circle-fill variant="info"></b-icon-info-circle-fill>
                  {{ $t('license.slot.enc_note') }}
                </div>
              </transition>
              <!-- === Предупреждение: добавление звездочки в Pers ============ -->
              <transition name="fade">
                <div v-if="(isPersonalTrialChanged() || isPersonalNonTrialChanged()) && isWildcardInserted()"
                     class="mt-3">
                  <b-icon-info-circle-fill variant="info"></b-icon-info-circle-fill>
                  {{ $t('license.slot.wild_note') }}
                </div>
              </transition>
              <!-- === Предупреждение: возможно редактирование оплаченного пока не скачено ============ -->
              <transition name="fade">
                <div v-if="isEditablePaidPresent()" class="mt-3">
                  <b-icon-info-circle-fill variant="info"></b-icon-info-circle-fill>
                  {{ $t('license.slot.edit_paid_note') }}
                </div>
              </transition>
              <!-- === Предупреждение: возможно редактирование трального пока не скачено ============ -->
              <transition name="fade">
                <div v-if="isEditableTrialPresent()" class="mt-3">
                  <b-icon-info-circle-fill variant="info"></b-icon-info-circle-fill>
                  {{ $t('license.slot.edit_trial_note') }}
                </div>
              </transition>
              <!-- === Connection Summary ========================== -->
              <summary-view v-if="isSummaryVisible()"
                            :item="conn._summary"
              ></summary-view>
            </div>
          </transition>
        </b-container>
      </template>
    </collapsible-card>
  </div>
</template>

<script>
import {BIconPlus, BIconClockFill, BIconInfoCircleFill} from "bootstrap-vue";
import CollapsibleCard from "../../CollapsibleCard.vue";
import SummaryView from "@/components/rdbm/SummaryView";
import EntityInput from "@/components/user-dashboard/rdbm/EntityInput";
import {
  ALLOWED_TYPES,
  isEncrypted,
  entHasReadable,
  conExpired,
  localNames,
  conValidUntilFormatted,
  conTypeWithState,
  conCanAddEntity,
  WILDCARD,
  conPaidNamesRemaining,
  isConNonPersonalTrial,
  isPersonalTrial,
  conType,
  conSummaryVisible,
  isNonPersonalTrial,
  isConPaid,
  entIsWildcard,
  conValidUntilDate,
  conValidUntilDistance
} from "@/components/rdbm/license.js";
import {distanceFromParsed, isoDate} from "@/util/time";
import {DateTime} from "luxon";
import I18n from "@/i18n";

export default {
  name: "ConnectionView",
  components: {
    BIconPlus,
    BIconInfoCircleFill,
    CollapsibleCard,
    SummaryView,
    BIconClockFill,
    EntityInput
  },
  props: ["license", "conn", "conn_idx"],
  data() {
    return {
      types: ALLOWED_TYPES
    };
  },
  methods: {
    isExpired: function () {
      return conExpired(this.license.data);
    },
    isLocalNames: function () {
      return localNames(this.license.data);
    },
    isNeverSaved: function () {
      return this.license.data._new ? true : false;
    },
    isPersonalTrial: function () {
      return isPersonalTrial(this.license.data);
    },
    isNonPersonalTrial: function () {
      return isNonPersonalTrial(this.license.data)
    },
    isAnyTypeTrial: function () {
      return isPersonalTrial(this.license.data) || isNonPersonalTrial(this.license.data)
    },
    isSummaryVisible: function () {
      return conSummaryVisible(this.conn);
    },
    isPersonalTrialChanged: function () {
      let license = this.license.data;
      return isPersonalTrial(license) && license.connections.length > 0 && license.connections.find(c => c.entities.length > 0);
    },
    isPersonalNonTrialChanged: function () {
      let license = this.license.data;
      return isNonPersonalTrial(license) && license.isChanged();
    },
    isWildcardInserted: function () {
      return this.license.data.connections.length > 0 &&
          this.license.data.connections.find(c =>
              c.entities.length > 0 &&
              c.entities.find(e => e._new && this.license.isWildcardEntity(e))
          );
    },
    isEditablePaidPresent: function () {
      return isConPaid(this.conn) && this.conn.entities.find(e => !e._new && !e.locked && !entIsWildcard(e))
    },
    isEditableTrialPresent: function () {
      return isConNonPersonalTrial(this.conn) && this.conn.entities.find(e => !e._new && !e.locked && !entIsWildcard(e))
    },
    canAddEntity: function () {
      return conCanAddEntity(this.conn, this.license.data.package);
    },
    connExpired: function (conn) {
      return conExpired(conn);
    },
    connTypeWithState: function (conn, conn_idx) {
      if (this.isPersonalTrialChanged() && conn_idx == 0) {
        let current = isoDate(conn.valid_until);
        let now = DateTime.now().setZone('utc');
        let truncated = now.plus({days: this.license.data.package.evaluation_days}).minus({seconds: 1});
        if (truncated.toMillis() > current.toMillis()) {
          truncated = current
        }

        return conType(conn) + ': ' + I18n.t('license.slot.states.until_to', [I18n.d(new Date(truncated.toMillis()), "short")]) +
            " (" +
            distanceFromParsed(truncated) +
            ")"
      } else {
        return conTypeWithState(conn);
      }
    },
    hasUnencryptedNames: function () {
      return (
          isEncrypted(this.license.data) &&
          this.conn.entities.find(
              (e) => e.name.trim() != WILDCARD && entHasReadable(e)
          )
      );
    },
    handleAddEntityClick: function (conn, conn_idx) {
      this.license.addEntity(conn, conn_idx);
    },
    handleAddSlotClick: function (conn_idx) {
      this.$emit('add-new-slot', conn_idx);
    },
    conValidUntilDate: function () {
      return conValidUntilDate(this.conn)
    },
    conValidUntilDistance: function () {
      return conValidUntilDistance(this.conn)
    },
    conValidUntilFormatted: function () {
      return conValidUntilFormatted(this.conn);
    },
    conPaidNamesRemaining: function () {
      return conPaidNamesRemaining(this.conn, this.license.data.package);
    },
    showSecondBadge: function () {
      return !isPersonalTrial(this.license.data) && isConNonPersonalTrial(this.conn)
    }
  },
};
</script>

<style scoped>
.add-btn-wrapper {
  display: grid;
  grid-template-columns: max-content auto;
}


.slot:not(:first-child) {
  margin-top: 3em;
}

.slot .container {
  padding: 0;
}

.slot .input-group {
  margin-bottom: 0.5em;
}

.slot .input-group:last-child,
.slot .desc {
  font-size: 90%;
  opacity: 0.7;
  margin-bottom: 1em;
}

.separator {
  display: flex;
}

.separator {
  flex-grow: 4;
}
</style>